import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Sparkles, ArrowRight, Car, Users, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { getTierPricing } from '../../config/pricing';
import { findSuggestedTier, getTierSuggestionDetails, TIER_LIMITS } from '../../utils/tierSuggestion';
import { normalizePlanTier, TIER_DISPLAY_NAMES } from '../../utils/planTier';

/**
 * ActionBlockedModal - Shown when user tries to perform an action that is blocked
 * because they've exceeded their tier limits.
 * 
 * This provides a CLEAR explanation of why the action failed and shows a DYNAMIC
 * tier suggestion that will actually work for their needs.
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {function} props.onOpenChange - Callback to change open state
 * @param {string} props.action - The action that was blocked (e.g., "add expense", "create co-host")
 * @param {Object} props.limitDetails - Details from the 402 error response
 * @param {number} props.limitDetails.currentVehicles - Current vehicle count
 * @param {number} props.limitDetails.vehicleLimit - Current tier's vehicle limit
 * @param {string} props.limitDetails.currentTier - User's current tier
 * @param {number} props.limitDetails.currentTeamMembers - Current team member count
 * @param {number} props.limitDetails.teamLimit - Current tier's team limit
 */
export default function ActionBlockedModal({
  open,
  onOpenChange,
  action = 'perform this action',
  limitDetails = {},
}) {
  const navigate = useNavigate();

  const {
    currentVehicles = 0,
    vehicleLimit = 10,
    currentTier = 'trial',
    currentTeamMembers = 1,
    teamLimit = 1,
  } = limitDetails;

  // Get dynamic tier suggestion that considers ALL needs
  const suggestedTier = findSuggestedTier({
    vehicles: currentVehicles,
    teamMembers: currentTeamMembers,
  });

  const _suggestionDetails = getTierSuggestionDetails(
    { vehicles: currentVehicles, teamMembers: currentTeamMembers },
    currentTier
  );

  const suggestedPlan = getTierPricing(suggestedTier);
  const currentLimits =
    TIER_LIMITS[normalizePlanTier(currentTier)] || TIER_LIMITS.free;

  const handleUpgrade = () => {
    onOpenChange(false);
    navigate('/settings/billing');
  };

  const handleManageVehicles = () => {
    onOpenChange(false);
    navigate('/vehicle-management');
  };

  // Tier styling
  const tierStyles = {
    solo: { bg: 'bg-indigo-600', bgLight: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200' },
    team: { bg: 'bg-purple-600', bgLight: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200' },
    growth: { bg: 'bg-emerald-600', bgLight: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' },
    enterprise: { bg: 'bg-amber-600', bgLight: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' },
    full_enterprise: { bg: 'bg-violet-600', bgLight: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-200' },
  };
  const styles = tierStyles[suggestedTier] || tierStyles.team;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="w-5 h-5" />
            Action Blocked
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* What was blocked */}
          <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-amber-800">
                Unable to {action}
              </p>
              <p className="text-sm text-amber-600">
                You've exceeded your plan's limits
              </p>
            </div>
          </div>

          {/* Current usage vs limits */}
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
            <p className="font-medium text-gray-700 text-sm">Your current usage:</p>
            
            {/* Vehicles */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Car className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Vehicles</span>
              </div>
              <div className="text-sm">
                <span className={currentVehicles > vehicleLimit ? 'text-red-600 font-semibold' : 'text-gray-700'}>
                  {currentVehicles}
                </span>
                <span className="text-gray-400"> / {vehicleLimit === Infinity ? '∞' : vehicleLimit}</span>
                {currentVehicles > vehicleLimit && (
                  <span className="text-red-500 text-xs ml-1">(+{currentVehicles - vehicleLimit} over)</span>
                )}
              </div>
            </div>

            {/* Team Members (only show if relevant) */}
            {currentLimits.teamSize > 1 && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-600">Team Members</span>
                </div>
                <div className="text-sm">
                  <span className={currentTeamMembers > teamLimit ? 'text-red-600 font-semibold' : 'text-gray-700'}>
                    {currentTeamMembers}
                  </span>
                  <span className="text-gray-400"> / {teamLimit === Infinity ? '∞' : teamLimit}</span>
                </div>
              </div>
            )}
          </div>

          {/* Suggested Plan */}
          <div className={`p-4 ${styles.bgLight} border ${styles.border} rounded-lg`}>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className={`w-4 h-4 ${styles.text}`} />
              <span className={`font-medium ${styles.text}`}>Recommended Plan</span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-gray-900">{TIER_DISPLAY_NAMES[suggestedTier] || suggestedTier} Plan</p>
                <p className="text-sm text-gray-600">
                  {suggestedPlan.vehicles === 'Unlimited' ? 'Unlimited' : `Up to ${suggestedPlan.vehicles}`} vehicles
                  {suggestedPlan.teamMembers > 1 && ` • ${suggestedPlan.teamMembers} team members`}
                </p>
              </div>
              <div className="text-right">
                <p className={`text-xl font-bold ${styles.text}`}>
                  ${suggestedPlan.price?.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500">/month</p>
              </div>
            </div>
          </div>

          {/* Alternative: Manage vehicles */}
          <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
            <p className="font-medium text-gray-700 mb-1">Alternative options:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-500">
              <li>Deactivate vehicles you've sold or totaled</li>
              <li>Remove vehicles you no longer manage</li>
            </ul>
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            variant="outline"
            onClick={handleManageVehicles}
            className="flex-1"
          >
            <Car className="w-4 h-4 mr-2" />
            Manage Vehicles
          </Button>
          <Button
            onClick={handleUpgrade}
            className={`flex-1 ${styles.bg} hover:opacity-90 text-white`}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Upgrade Now
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
